import { NetworkStatus } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useGetAnalysisByTokenLazyQuery } from 'generated/graphql';
import { useCallback } from 'react';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { parseEQL } from 'v2/core/repository/eql/parser';
import { useFromWhereClause } from 'v2/core/services/queryBuilder/useFromWhereClause';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';
import normaliseQuery from 'v2/querybuilder/services/denormalise/normaliseQuery';
import { trackCustomFilterEvents } from '../helpers';
import { QueryBuilderActionType } from '../store/queryBuilderActionType';
import { useQueryBuilderStore } from '../store/useQueryBuilderStore';
import { useGenerateUpdateFilters } from './useGenerateUpdateFilters';
import { useSaveFilterIDToValueMapInStore } from './useSaveFilterIDToValueMapInStore';

interface FetchCustomFilterOptions {
  onError?: () => void;
}

export const useFetchCustomFilter = ({
  onError,
}: FetchCustomFilterOptions = {}) => {
  const {
    tenant: { id: tenantID, timezone },
    id: userID,
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const { data: sourceDetailsMap } = useGetSourceDetailsMap();
  const fromWhereClause = useFromWhereClause();
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const whereClauseInStore = useQueryBuilderStore(
    ({ metadata }) => metadata.whereClause,
  );
  const rootID = useQueryBuilderStore(({ query }) => query.rootID);
  const generateUpdateFilters = useGenerateUpdateFilters();

  useSaveFilterIDToValueMapInStore({
    whereClause: whereClauseInStore,
  });

  const [getAnalysisByToken, { data, error, loading }] =
    useGetAnalysisByTokenLazyQuery({
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    });

  const fetchAnalysis = useCallback(
    async (token: string) => {
      try {
        const resp = await getAnalysisByToken({
          variables: {
            token,
            tenantID,
            userID,
          },
        });
        if (
          Boolean(resp.error) ||
          resp.networkStatus === NetworkStatus.error // Checking for network status, because error is not always present due to a bug in apollo client
        ) {
          onError
            ? onError()
            : showErrorNotification({
                contentComponent: 'Failed to fetch the Custom Filter',
                props: {
                  timeout: 10 * 1000, // 10 seconds
                },
              });
          return;
        }

        if (!resp.data?.getAnalysisByToken) {
          return;
        }

        return {
          eqlExpression: resp.data.getAnalysisByToken.context.eqlExpression,
          title: resp.data.getAnalysisByToken.title,
        };
      } catch {
        // do nothing, already handled
      }
    },
    [getAnalysisByToken, onError, showErrorNotification, tenantID, userID],
  );

  const fetchAndApplyFilters = useCallback(
    async (token?: string) => {
      if (!token) {
        return normaliseQuery(fromWhereClause(EmptyWhereClause, rootID));
      }

      dispatch({
        type: QueryBuilderActionType.SET_METADATA,
        payload: {
          isLoading: true,
        },
      });

      const resp = await fetchAnalysis(token);

      const updatedNodes = generateUpdateFilters(resp?.eqlExpression);

      trackCustomFilterEvents({
        eventName: 'Custom Filter Applied',
        filterName: resp?.title ?? '',
        eql: resp?.eqlExpression ?? '',
      });

      return updatedNodes;
    },
    [dispatch, fetchAnalysis, fromWhereClause, generateUpdateFilters, rootID],
  );

  const analysis = data?.getAnalysisByToken;

  const getWhereClauseForTheFilter = useCallback(
    async (token?: string) => {
      if (!token) {
        return;
      }

      const resp = await fetchAnalysis(token);

      if (!sourceDetailsMap || !resp) {
        return EmptyWhereClause;
      }

      const whereClause = parseEQL(
        resp.eqlExpression,
        sourceDetailsMap,
        timezone,
      ).whereClause;

      return whereClause;
    },
    [fetchAnalysis, sourceDetailsMap, timezone],
  );

  return [
    fetchAndApplyFilters,
    {
      analysis,
      getWhereClauseForTheFilter,
      loading,
      error,
    },
  ] as const;
};
